import axios from 'axios';
import Cookies from 'universal-cookie';
import { INIT_MUTATION } from '../../graphql/queries';

// Shared in-flight init promise so concurrent 428s trigger only one init call.
let inFlightInit = null;

// Latest publicKey returned by `init`. Kept here so password encryption always
// uses the key that matches the server's current session — including after a
// 428-triggered re-init.
let currentPublicKey = '';
export const getPublicKey = () => currentPublicKey;

const captureInit = (init) => {
  const key = init?.data?.publicKey;
  if (key) currentPublicKey = key;
  return init;
};

const postGraphQL = (query, variables) => {
  const cookies = new Cookies();
  const csrfToken = cookies.get('X-CSRF-TOKEN');
  const accessToken = cookies.get('accessToken');

  const headers = { 'Content-Type': 'application/json' };
  if (csrfToken) headers['X-CSRF-TOKEN'] = csrfToken;
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return axios.post(
    `${process.env.REACT_APP_API_ENDPOINT}/graphql`,
    { query, variables },
    { headers, withCredentials: true }
  );
};

const isPreconditionRequired = (err) =>
  err?.response?.status === 428 ||
  err?.errors?.some((e) => e?.extensions?.statusCode === 428);

const refreshInit = () => {
  if (!inFlightInit) {
    inFlightInit = postGraphQL(INIT_MUTATION, {})
      .then((res) => {
        const init = res?.data?.data?.init;
        if (init?.status === 'OK') {
          captureInit(init);
          return init;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        inFlightInit = null;
      });
  }
  return inFlightInit;
};

const resolveVariables = (variables) =>
  typeof variables === 'function' ? variables() : variables;

const performRequest = async (query, variables, retried) => {
  try {
    const { data: resp } = await postGraphQL(query, resolveVariables(variables));
    if (resp?.data && !resp.errors) {
      // Keep `currentPublicKey` in sync when callers run init directly.
      if (query === INIT_MUTATION) captureInit(resp.data?.init);
      return resp.data;
    }
    throw resp;
  } catch (error) {
    if (isPreconditionRequired(error) && !retried && query !== INIT_MUTATION) {
      if (await refreshInit()) {
        // Re-enter so — when `variables` is a thunk — it is re-evaluated
        // against the newly-captured publicKey before retrying.
        return performRequest(query, variables, true);
      }
    }
    const errorToHandle = error?.response?.data?.errors
      ? error.response.data
      : error;
    // eslint-disable-next-line no-console
    console.error('[GraphQL] Request error:', errorToHandle);
    throw errorToHandle;
  }
};

const request = (query, variables = {}) => performRequest(query, variables, false);

export const executeQuery = (client, query, variables) =>
  client.request(query, variables);

export const graphqlClient = { request };
